import React, { useState, useRef } from 'react';
import { CreditCard, Calendar, Clock, Printer, FileDown, ListChecks, Loader2 } from 'lucide-react';
import { useVendorPayments } from '../../hooks/useVendorPayments';
import { openPrintWindow, saveDomAsPdf, escapeHtml } from '../../utils/reportExport';
import { useToastContext } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import PrivateBookingBadge from '../../components/shared/PrivateBookingBadge';
import { isPrivateBooking, PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';
import {
 paymentCatalogAmount,
 paymentVendorShareDisplay,
} from '../../utils/formatMoney';

const STATUS_TABS = [
 { key: 'all', label: 'All' },
 { key: 'completed', label: 'Completed' },
 { key: 'pending', label: 'Pending' },
 { key: 'failed', label: 'Failed' },
];

const VendorPayments = () => {
 const reportRef = useRef(null);
 const { user } = useAuth();
 const { success, error: showError } = useToastContext();
 const [pdfBusy, setPdfBusy] = useState(false);
 const [statusFilter, setStatusFilter] = useState('all');
 const [currentPage, setCurrentPage] = useState(1);
 
 const {
 payments,
 isLoading,
 error,
 pagination,
 formatAmount,
 getStatusIcon,
 getStatusColor
 } = useVendorPayments(statusFilter, currentPage);

 // Format date to be more readable
 const formatDate = (dateString) => {
 if (!dateString) return 'N/A';
 const date = new Date(dateString);
 return date.toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'long',
 day: 'numeric'
 });
 };

 // Format time
 const formatTime = (dateString) => {
 if (!dateString) return 'N/A';
 const date = new Date(dateString);
 return date.toLocaleTimeString('en-US', {
 hour: '2-digit',
 minute: '2-digit'
 });
 };

 /** Payout shown to vendor: amount minus platform fee (see API `vendor_payout`). */
 const vendorPayoutAmount = (payment) => {
 if (isPrivateBooking(payment) || payment?.counts_toward_platform_payout === false) {
 return 0;
 }
 if (payment?.vendor_payout != null && payment.vendor_payout !== '') {
 const v = parseFloat(payment.vendor_payout);
 if (!Number.isNaN(v)) return payment.vendor_payout;
 }
 return paymentVendorShareDisplay(payment);
 };

 const displayPaymentAmount = (payment) => {
 if (isPrivateBooking(payment)) {
 return paymentCatalogAmount(payment);
 }
 return vendorPayoutAmount(payment);
 };

 const fmtAmount = (v) => {
 const n = typeof v === 'number' ? v : parseFloat(v);
 if (Number.isNaN(n)) return '0.00';
 return n.toFixed(2);
 };

 const buildPrintBodyHtml = () => {
 const vendorLine = user?.name || user?.email ? escapeHtml(user.name || user.email) : '';
 const filterLabel =
 statusFilter === 'all'
 ? 'All payments'
 : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} payments`;
 const pageLine =
 pagination && pagination.last_page > 1
 ? `List: page ${pagination.current_page} of ${pagination.last_page} (${pagination.total ?? ''} total).`
 : '';

 const rowsHtml = payments
 .map((payment) => {
 const payout = fmtAmount(displayPaymentAmount(payment));
 const privateLabel = isPrivateBooking(payment) ? 'Private' : '';
 const status = escapeHtml(payment.status || '');
 const when = escapeHtml(`${formatDate(payment.created_at)} ${formatTime(payment.created_at)}`);
 const booking = escapeHtml(payment.booking?.booking_reference || (payment.booking_id ? `#${payment.booking_id}` : '—'));
 const client = escapeHtml(payment.booking?.client?.name || '—');
 const ref = escapeHtml(payment.payment_reference || payment.transaction_id || '—');
 return `<tr>
 <td><strong>$${escapeHtml(String(payout))}</strong>${privateLabel ? ` <span style="font-size:10px;color:#4338ca">(${privateLabel})</span>` : ''}</td>
 <td>${status}</td>
 <td>${when}</td>
 <td>${booking}</td>
 <td>${client}</td>
 <td style="font-size:10px">${ref}</td>
 </tr>`;
 })
 .join('');

 return `
 <h1>Payments</h1>
 <p class="muted">${vendorLine ? `Vendor: ${vendorLine}<br/>` : ''}${escapeHtml(filterLabel)} · Generated ${escapeHtml(
 new Date().toLocaleString()
 )}</p>
 ${pageLine ? `<p class="muted">${escapeHtml(pageLine)}</p>` : ''}
 <table>
 <thead>
 <tr><th>Your payout</th><th>Status</th><th>Date & time</th><th>Booking</th><th>Client</th><th>Reference</th></tr>
 </thead>
 <tbody>
 ${rowsHtml || '<tr><td colspan="6">No payments on this page.</td></tr>'}
 </tbody>
 </table>
 `;
 };

 const handlePrint = () => {
 const r = openPrintWindow('Payments', buildPrintBodyHtml());
 if (!r.ok) showError('Allow pop-ups to print this report.');
 };

 const handlePdf = async () => {
 if (!reportRef.current) return;
 setPdfBusy(true);
 try {
 await saveDomAsPdf(reportRef.current, `vendor-payments-${new Date().toISOString().slice(0, 10)}`);
 success('PDF downloaded.');
 } catch (e) {
 showError(e?.message || 'Could not generate PDF.');
 } finally {
 setPdfBusy(false);
 }
 };

 const emptyCopy =
 statusFilter === 'all'
 ? 'Payments from bookings and invoices will show up here once clients pay.'
 : `No ${statusFilter} payments right now.`;

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Payments"
 subtitle="Review each payment tied to a booking or invoice; platform payouts exclude private bookings."
 actions={
 <>
 <button
 type="button"
 onClick={handlePrint}
 className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-border bg-surface text-gray-800 rounded-md hover:bg-surface-muted transition-colors text-sm w-full sm:w-auto justify-center"
 >
 <Printer className="w-4 h-4" />
 <span>Print</span>
 </button>
 <button
 type="button"
 onClick={handlePdf}
 disabled={pdfBusy || isLoading}
 className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto justify-center disabled:opacity-50"
 >
 <FileDown className="w-4 h-4" />
 <span>{pdfBusy ? 'PDF…' : 'Download PDF'}</span>
 </button>
 </>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.payments.title}
 steps={VENDOR_PAGE_GUIDES.payments.steps}
 icon={ListChecks}
 />

 <div ref={reportRef} className="space-y-5">
 <div className="rounded-xl border border-border bg-surface p-4 sm:p-5">
 <p className="text-xl font-bold text-ink">Payments</p>
 {(user?.name || user?.email) && (
 <p className="text-sm text-ink-secondary mt-1">{user.name || user.email}</p>
 )}
 <p className="text-xs text-ink-tertiary mt-1">
 Filter:{' '}
 {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} ·{' '}
 {new Date().toLocaleString()}
 </p>
 </div>

 <div className="product-panel overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Payment status"
 >
 {STATUS_TABS.map((tab) => {
 const isActive = statusFilter === tab.key;
 return (
 <button
 key={tab.key}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => {
 setStatusFilter(tab.key);
 setCurrentPage(1);
 }}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 </button>
 );
 })}
 </div>
 </div>

 <div className="p-4 sm:p-5">
 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading payments…</p>
 </div>
 ) : error ? (
 <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
 Failed to load payments. Please try again.
 </div>
 ) : payments.length > 0 ? (
 <div className="space-y-3">
 {payments.map((payment) => {
 const isPrivate = isPrivateBooking(payment);
 return (
 <div
 key={payment.id}
 className="product-surface-muted p-3 sm:p-4 transition-colors hover:border-ink"
 >
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-start gap-3 mb-2">
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-gray-400">
 <CreditCard className="w-4 h-4" />
 </div>
 <div className="min-w-0">
 <div className="flex flex-wrap items-center gap-2">
 <p className="font-semibold text-ink tabular-nums">
 ${formatAmount(displayPaymentAmount(payment))}
 </p>
 {isPrivate && <PrivateBookingBadge />}
 </div>
 <p className="text-xs text-ink-tertiary mt-0.5">
 {isPrivate ? 'Collected offline' : 'Your payout'}
 </p>
 {isPrivate && (
 <p className="text-xs text-ink mt-0.5">{PRIVATE_PAYOUT_EXCLUDED_NOTE}</p>
 )}
 <p className="text-sm text-ink-tertiary">
 {payment.booking_id ? `Booking #${payment.booking_id}` : 'Payment'}
 </p>
 </div>
 </div>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm text-ink-secondary">
 <span className="inline-flex items-center gap-1.5">
 <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
 {formatDate(payment.created_at)}
 </span>
 <span className="inline-flex items-center gap-1.5">
 <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
 {formatTime(payment.created_at)}
 </span>
 {payment.payment_method && (
 <span className="capitalize">
 {payment.payment_method}
 </span>
 )}
 {payment.payment_reference && (
 <span className="text-ink-tertiary text-xs font-mono">
 Ref: {payment.payment_reference.substring(0, 8)}
 </span>
 )}
 </div>
 {/* Additional Payment Details */}
 <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-ink-secondary">
 {payment.booking?.booking_reference && (
 <div>
 <span className="font-medium text-ink">Booking:</span> {payment.booking.booking_reference}
 </div>
 )}
 {payment.booking?.client && (
 <div>
 <span className="font-medium text-ink">Client:</span> {payment.booking.client.name}
 </div>
 )}
 {payment.payment_type && (
 <div>
 <span className="font-medium text-ink">Type:</span> <span className="capitalize">{payment.payment_type.replace('_', ' ')}</span>
 </div>
 )}
 {payment.currency && (
 <div>
 <span className="font-medium text-ink">Currency:</span> {payment.currency.toUpperCase()}
 </div>
 )}
 {(parseFloat(payment.booking?.discount_amount) || 0) > 0 && (
 <div>
 <span className="font-medium text-ink">Discount{payment.booking?.discount_code?.code ? ` (${payment.booking.discount_code.code})` : ''}:</span>{' '}
 <span className="text-emerald-600">-${formatAmount(payment.booking.discount_amount)}</span>
 </div>
 )}
 </div>
 </div>
 <div className="flex items-center gap-3 shrink-0">
 <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${getStatusColor(payment.status)}`}>
 {getStatusIcon(payment.status)}
 {payment.status || 'unknown'}
 </span>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 ) : (
 <div className="py-16 sm:py-20 text-center px-4">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <CreditCard className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 No payments found
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto leading-relaxed">
 {emptyCopy}
 </p>
 </div>
 )}

 {/* Pagination */}
 {pagination && pagination.last_page > 1 && (
 <div className="mt-5 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-border bg-surface-muted p-4">
 <div className="text-sm text-ink-secondary">
 Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
 </div>
 
 <div className="flex items-center space-x-2">
 <button
 type="button"
 onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
 disabled={pagination.current_page === 1}
 className="px-3 py-1.5 text-xs border border-border rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface text-ink transition-colors"
 >
 Previous
 </button>
 
 <div className="flex items-center space-x-1">
 {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
 let page;
 if (pagination.last_page <= 5) {
 page = i + 1;
 } else if (pagination.current_page <= 3) {
 page = i + 1;
 } else if (pagination.current_page >= pagination.last_page - 2) {
 page = pagination.last_page - 4 + i;
 } else {
 page = pagination.current_page - 2 + i;
 }
 
 return (
 <button
 key={page}
 type="button"
 onClick={() => setCurrentPage(page)}
 className={`px-3 py-1.5 text-xs border rounded-xl transition-colors ${ pagination.current_page === page ? 'bg-black text-white border-black' : 'border-gray-300 text-ink hover:bg-surface' }`}
 >
 {page}
 </button>
 );
 })}
 </div>
 
 <button
 type="button"
 onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
 disabled={pagination.current_page === pagination.last_page}
 className="px-3 py-1.5 text-xs border border-border rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface text-ink transition-colors"
 >
 Next
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default VendorPayments;
